import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Sun, CalendarRange, Save } from "lucide-react";
import { eur, hours, fmtDate } from "./shared";

const STATUS = {
  current: { label: "En cours", cls: "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]" },
  upcoming: { label: "À venir", cls: "bg-[#EFF6FF] text-[#0066FF] border-[#BFDBFE]" },
  past: { label: "Terminée", cls: "bg-[#F8F9FA] text-[#868E96] border-[#DEE2E6]" },
};

export function SiteSeasonPanel({ site, seasonStats, seasonalMode, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const periods = site?.opening_periods || [];

  const startEdit = () => { setRows(periods.map((p) => ({ ...p }))); setEditing(true); };
  const addRow = () => setRows([...rows, { id: "", label: "", date_from: "", date_to: "" }]);
  const save = async () => {
    if (rows.some((r) => !r.date_from || !r.date_to)) return;
    await onSave(rows);
    setEditing(false);
  };

  if (!seasonalMode) {
    return (
      <div className="bg-[#F8F9FA] border border-dashed border-[#DEE2E6] p-6 text-sm text-[#495057]" data-testid="site-season-off">
        <div className="flex items-center gap-2 font-semibold text-[#0A0A0A] mb-1"><Sun className="h-4 w-4 text-[#FFD700]" /> Mode saisonnier désactivé</div>
        Activez le mode saisonnier depuis la vue siège pour définir des périodes d'ouverture et suivre les statistiques par saison.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-season">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-[#495057]">
          {periods.length === 0 ? "Aucune période définie : l'établissement est considéré ouvert toute l'année." : `${periods.length} période${periods.length > 1 ? "s" : ""} d'ouverture`}
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={startEdit} data-testid="site-season-edit" className="h-9 border-[#0A0A0A]">
            <CalendarRange className="h-3.5 w-3.5 mr-1" /> Modifier les périodes
          </Button>
        )}
      </div>

      {editing ? (
        <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-4 space-y-3" data-testid="site-season-editor">
          {rows.length === 0 && <div className="text-xs text-[#868E96]">Ajoutez une première période (ex. Été : 01/06 → 15/09).</div>}
          {rows.map((r, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_160px_160px_40px] gap-2 items-center" data-testid={`site-season-row-${i}`}>
              <Input placeholder="Libellé (Été, Hiver…)" value={r.label} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-10 bg-white" data-testid={`site-season-label-${i}`} />
              <Input type="date" value={r.date_from} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, date_from: e.target.value } : x))} className="h-10 bg-white" data-testid={`site-season-from-${i}`} />
              <Input type="date" value={r.date_to} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, date_to: e.target.value } : x))} className="h-10 bg-white" data-testid={`site-season-to-${i}`} />
              <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="h-10 w-10 flex items-center justify-center border border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" data-testid={`site-season-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={addRow} data-testid="site-season-add" className="h-9"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une période</Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-9">Annuler</Button>
            <Button size="sm" onClick={save} data-testid="site-season-save" className="h-9 bg-[#002FA7] hover:bg-[#002175] text-white"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {(seasonStats || []).map((p) => {
            const st = STATUS[p.status] || STATUS.upcoming;
            return (
              <div key={p.id} className="bg-white border border-[#DEE2E6] p-4" data-testid={`site-season-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-black text-lg">{p.label || "Période"}</div>
                    <div className="font-mono text-xs text-[#495057]">{fmtDate(p.date_from)} → {fmtDate(p.date_to)} · {p.days} j</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <Mini label="Heures" value={hours(p.heures)} />
                  <Mini label="CA" value={eur(p.ca)} />
                  <Mini label="Saisonniers" value={p.nb_saisonniers} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-[#F8F9FA] border border-[#DEE2E6] py-2 px-1">
      <div className="text-[9px] uppercase tracking-wider text-[#868E96] font-bold">{label}</div>
      <div className="font-mono font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
}
